/**
 * PHOTOS §4: the `PhotoPort` the menus are handed. A picture is looked for on this device first - waiting for one
 * still being developed, since the memorial beat asks the moment the ranger falls - then, when cloud saves are on,
 * on the save server, and a copy fetched from there is kept here (it is already uploaded). Never rejects.
 */

import type { PhotoPort } from '../../ui/screens/photoPort';
import type { Developing } from './camera';
import type { PhotoStore } from './store';
import type { PhotoCloud } from './sync';
import type { PhotoTransport } from './transport';
import { isPhotoId } from './types';

export interface PortDeps {
  store: PhotoStore;
  transport: PhotoTransport;
  cloud: Pick<PhotoCloud, 'enabled' | 'code'>;
  /** Where the public wall serves its pictures: the origin the page came from. */
  origin: string;
  developing: Developing;
  /** `URL.createObjectURL` in the browser. */
  objectUrl(jpeg: Blob): string;
  now(): number;
}

interface Found {
  jpeg: Blob | null;
  /** False when the server could not be asked: worth asking again, so not remembered. */
  final: boolean;
}

export function createPhotoPort(deps: PortDeps): PhotoPort {
  const { store, transport, cloud, developing } = deps;
  const found = new Map<string, Promise<Blob | null>>();
  const urls = new Map<string, Promise<string | null>>();

  async function look(id: string): Promise<Found> {
    await developing.get(id);
    const here = await store.get(id);
    if (here !== null) return { jpeg: here.jpeg, final: true };
    const code = cloud.enabled() ? cloud.code() : null;
    if (code === null) return { jpeg: null, final: true };
    const got = await transport.get(code, id);
    if (got.kind === 'unreachable') return { jpeg: null, final: false };
    if (got.kind === 'none') return { jpeg: null, final: true };
    // Kept, and marked uploaded: the server has it, so it is never sent back.
    await store.keep({ id, jpeg: got.jpeg, at: deps.now(), uploaded: true });
    return { jpeg: got.jpeg, final: true };
  }

  /** One look per id per page, unless the server could not be reached (then the next call asks again). */
  function find(id: string): Promise<Blob | null> {
    if (!isPhotoId(id)) return Promise.resolve(null);
    const known = found.get(id);
    if (known !== undefined) return known;
    const finding = look(id)
      .catch((): Found => ({ jpeg: null, final: false }))
      .then(({ jpeg, final }) => {
        if (!final) found.delete(id);
        return jpeg;
      });
    found.set(id, finding);
    return finding;
  }

  function objectUrl(jpeg: Blob): string | null {
    try {
      return deps.objectUrl(jpeg);
    } catch {
      return null;
    }
  }

  return {
    url(id) {
      const known = urls.get(id);
      if (known !== undefined) return known;
      // One object URL per id for the life of the page: never revoked, as a screen may still be showing it.
      const making = find(id).then((jpeg) => (jpeg === null ? null : objectUrl(jpeg)));
      urls.set(id, making);
      void making.then((url) => {
        if (url === null) urls.delete(id);
      });
      return making;
    },

    async jpeg(id) {
      const jpeg = await find(id);
      if (jpeg === null) return null;
      try {
        return new Uint8Array(await jpeg.arrayBuffer());
      } catch {
        return null;
      }
    },

    publicUrl: ({ by, id }) => `${deps.origin}/api/memorial/photo/${encodeURIComponent(by)}/${encodeURIComponent(id)}`,
  };
}
